
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
    BarChart3,
    TrendingUp,
    Users,
    IndianRupee,
    PieChart
} from 'lucide-react';
import {
    HRAnalyticsDashboard,
    AttritionAnalysis,
    RecruitmentFunnel,
    SalaryTrends
} from './analytics-reporting';
import Drawer from '../../components/common/Drawer'; // Assuming generic Drawer exists

const HRAnalyticsModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for tabs
    const currentTab = searchParams.get('tab') || 'hrAnalyticsDashboard';

    // State for Drawer (Drilldown/Schedule)
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Sync Drawer state with URL action
    useEffect(() => {
        if (action === 'viewDrilldown' || action === 'scheduleReport') {
            setIsDrawerOpen(true);
        } else {
            setIsDrawerOpen(false);
        }
    }, [action]);

    // Handle Tab Change
    const handleTabChange = (tabKey) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tabKey);
        // Clear action when switching tabs? usually yes.
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    };

    // Close Drawer
    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id'); // Clean up ID as well
        setSearchParams(newParams);
        setIsDrawerOpen(false);
    };

    // Tabs Configuration
    const tabs = [
        {
            id: 'hrAnalyticsDashboard',
            label: 'HR Analytics Dashboard',
            icon: BarChart3,
            component: HRAnalyticsDashboard
        },
        {
            id: 'attritionAnalysis',
            label: 'Attrition Analysis',
            icon: TrendingUp,
            component: AttritionAnalysis
        },
        {
            id: 'recruitmentFunnel',
            label: 'Recruitment Funnel',
            icon: Users, // or Filter
            component: RecruitmentFunnel
        },
        {
            id: 'salaryTrends',
            label: 'Salary Trends',
            icon: IndianRupee,
            component: SalaryTrends
        }
    ];

    // Render Component for current tab
    const renderContent = () => {
        const activeTabObj = tabs.find(t => t.id === currentTab) || tabs[0];
        const Component = activeTabObj.component;
        return <Component />;
    };

    // Determine Drawer Content based on action
    const renderDrawerContent = () => {
        if (action === 'scheduleReport') {
            return (
                <div className="p-4">
                    <h3 className="text-lg font-bold mb-4">Schedule Report</h3>
                    <p className="text-gray-500">Report scheduling functionality coming soon.</p>
                    {/* Calendar/Form would go here */}
                </div>
            );
        }

        if (action === 'viewDrilldown') {
            return (
                <div className="p-4">
                    <h3 className="text-lg font-bold mb-4">Drilldown Details</h3>
                    <p className="text-gray-500">Details for ID: {actionId}</p>
                    {/* Employee List or Cohort List component would go here */}
                </div>
            );
        }

        return null;
    };

    return (
        <div className="flex flex-col h-full bg-gray-50 p-4 sm:p-6 font-sans space-y-6">
            {/* Module Header & Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 pt-6 sticky top-0 z-10">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-xs flex-shrink-0">
                            <BarChart3 className="w-5 h-5 stroke-[2]" />
                        </div>
                        <div>
                            <h1 className="text-[22px] font-bold text-[#0F2942] leading-tight font-sans">
                                HR Analytics
                            </h1>
                            <p className="text-[12px] text-[#64748B] font-medium font-sans mt-0.5">
                                Comprehensive HR insights, attrition, and recruitment metrics
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation (SaaS Segmented Pill Container) */}
                <div className="bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 inline-flex space-x-1.5 max-w-full overflow-x-auto no-scrollbar mb-2">
                    {tabs.map((tab) => {
                        const isActive = currentTab === tab.id;
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`
                                  py-2 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center space-x-2 whitespace-nowrap transition-all duration-200 font-sans cursor-pointer
                                  ${isActive
                                        ? 'bg-white text-blue-700 shadow-sm border border-slate-200/70 scale-[1.01]'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'}
                                `}
                            >
                                <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto">
                {renderContent()}
            </div>

            {/* Right-side Drawer */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                title={action === 'scheduleReport' ? 'Schedule Report' : 'Drilldown View'}
                width="w-96" // or 'w-[400px]' depending on Drawer component props
            >
                {renderDrawerContent()}
            </Drawer>
        </div>
    );
};

export default HRAnalyticsModule;
